import { useEffect, useState } from 'react';

import dayjs from 'dayjs';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { formatCurrency, formatNumber } from '@utils/utils.ts';
import AdminTokenUsageFilters from '@features/admin-token-usage/containers/AdminTokenUsageFilters';
import useAdminTokenUsageFilters from '@features/admin-token-usage/hooks/useAdminTokenUsageFilters.tsx';
import AdminTokenUsageTotals from '@features/admin-token-usage/containers/AdminTokenUsageTotals';

import useAdminTokenUsageData from '@features/admin-token-usage/hooks/useAdminTokenUsageData.tsx';
import AdminTokenUsageTotalTable from '@features/admin-token-usage/containers/AdminTokenUsageTotalTable';
import { AdminTokenUsageTotalTableRow } from '@features/admin-token-usage/types';
import styles from './index.module.scss';

function formatCategoryLabel(
  interval: string | number,
  dateStr: string
): string {
  const date = dayjs(dateStr);
  switch (interval) {
    case 'day':
    case 'week':
      return date.format('MMM D');
    case 'month':
      return date.format('MMM YYYY');
    case 'quarter': {
      const q = Math.ceil((date.month() + 1) / 3);
      return `Q${q} ${date.year()}`;
    }
    case 'year':
      return date.format('YYYY');
    default:
      return date.format('MMM D');
  }
}

function AdminTokenUsage() {
  const {
    showTokenCount,
    selectedInterval,
    documentResult,
    selectedDocumentTypes,
    startDate,
    endDate,
    setTotalTableData,
  } = useAdminTokenUsageFilters();

  const [chartOptions, setChartOptions] = useState<Highcharts.Options>({
    chart: {
      height: 500,
      type: 'line',
    },
    title: {
      text: 'Open AI Token Usage',
    },
    xAxis: {
      categories: [],
      title: { text: 'Month' },
    },
    yAxis: [
      {
        title: { text: '$ Cost' },
        labels: {
          formatter() {
            return formatCurrency(this.value as number);
          },
        },
      },
      {
        title: { text: 'Tokens' },
        opposite: true,
        visible: showTokenCount,
        labels: {
          formatter() {
            return formatNumber(this.value as number);
          },
        },
      },
    ] as Highcharts.YAxisOptions[],
    tooltip: {
      useHTML: true,
      formatter() {
        const value = this.y as number;
        const isCost =
          !this.series.name.includes('Tokens') &&
          !this.series.name.includes('Demands');
        const formattedValue = isCost
          ? formatCurrency(value, 2)
          : formatNumber(value);

        return `
          <b>${this.key}</b><br/>
          <span style="color:${this.color}">●</span> ${this.series.name}: ${formattedValue}<br/>
        `;
      },
    },
    series: [],
  });

  const { data, isLoading } = useAdminTokenUsageData({
    interval: selectedInterval.value,
    selectedDocumentTypes,
    startDate,
    endDate,
    category: documentResult.value,
  });

  useEffect(() => {
    if (isLoading || !data) return;

    const datasets = data;
    if (datasets.length === 0) return;

    const categories = datasets[0].map((item) =>
      formatCategoryLabel(selectedInterval.value, item.date)
    );

    const totalTableRows: AdminTokenUsageTotalTableRow[] = [];
    const series = datasets.flatMap((dataset, index) => {
      let name: string;
      if (index === 0) {
        name = 'Total';
      } else {
        const docTypeOption = selectedDocumentTypes[index - 1];
        name = docTypeOption?.label ?? 'Document Type';
      }

      const costData = dataset.map((v) => v.total_cost);
      const tokenData = dataset.map((v) => v.total_tokens);

      totalTableRows.push({
        name,
        avg: costData.reduce((acc, v) => acc + v, 0) / costData.length,
        values: costData,
        total: costData.reduce((acc, v) => acc + v, 0),
        formatFunction: (v: number) => formatCurrency(v, 2),
      });

      if (showTokenCount) {
        totalTableRows.push({
          name: `${name} - Tokens`,
          avg: tokenData.reduce((acc, v) => acc + v, 0) / tokenData.length,
          values: tokenData,
          total: tokenData.reduce((acc, v) => acc + v, 0),
          formatFunction: (v: number) => formatNumber(v),
        });
      }

      return [
        {
          name,
          data: costData,
          yAxis: 0,
          type: 'line',
          dataLabels: {
            enabled: true,
            formatter() {
              return formatCurrency(this.y as number, 2);
            },
          },
        } as Highcharts.SeriesOptionsType,
        showTokenCount
          ? ({
              name: `${name} - Tokens`,
              data: tokenData,
              yAxis: 1,
              type: 'line',
              dataLabels: {
                enabled: true,
                formatter() {
                  return formatNumber(this.y as number);
                },
              },
            } as Highcharts.SeriesOptionsType)
          : null,
      ].filter(Boolean) as Highcharts.SeriesOptionsType[];
    });

    // Ensure "Total" rows are always at the end of the table
    const totalRows = totalTableRows.filter(
      (row) => row.name === 'Total' || row.name === 'Total - Tokens'
    );
    const otherRows = totalTableRows.filter(
      (row) => row.name !== 'Total' && row.name !== 'Total - Tokens'
    );

    setTotalTableData({
      headers: categories,
      rows: [...otherRows, ...totalRows],
    });

    setChartOptions((prevOptions) => ({
      ...prevOptions,
      xAxis: {
        ...prevOptions.xAxis,
        categories,
        title: { text: selectedInterval.label },
      },
      series,
      yAxis: Array.isArray(prevOptions.yAxis)
        ? [
            { ...prevOptions.yAxis[0] },
            { ...prevOptions.yAxis[1], visible: showTokenCount },
          ]
        : prevOptions.yAxis,
    }));
  }, [
    data,
    isLoading,
    showTokenCount,
    selectedDocumentTypes,
    selectedInterval.value,
    selectedInterval.label,
    documentResult,
  ]);

  return (
    <div className={styles.chartContainer}>
      <AdminTokenUsageFilters />
      <HighchartsReact highcharts={Highcharts} options={chartOptions} />
      <AdminTokenUsageTotalTable />
      <AdminTokenUsageTotals />
    </div>
  );
}

export default AdminTokenUsage;
